import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { ConnectivityBadge } from "../../components/devices/ConnectivityBadge";
import { DevicePolicyControl } from "../../components/devices/DevicePolicyControl";
import { Alert } from "../../components/ui/Alert";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { DescriptionList } from "../../components/ui/DescriptionList";
import { JsonViewer } from "../../components/ui/JsonViewer";
import { ErrorState, LoadingState } from "../../components/ui/State";
import { Tabs } from "../../components/ui/Tabs";
import { ApiError } from "../../lib/api";
import {
  type AdminDeviceStatusTarget,
  devicesApi,
} from "../../lib/api/devices";
import { deviceNetworkPolicyApi } from "../../lib/api/network/policy";
import { lifecycleStatusLabel, lifecycleTone } from "../../lib/deviceLifecycle";
import { formatDateTime } from "../../lib/format";
import { t } from "../../lib/i18n";
import { invalidatePolicyViews } from "../../lib/policyQueries";
import { useLocale } from "../../lib/useLocale";
import { LIVE_REFRESH_INTERVAL_MS, useLiveNow } from "../../lib/useLiveNow";

type Tab = "overview" | "policy";

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t("errors." + err.code, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

interface DeviceDetailPanelProps {
  deviceId: string;
  onClose: () => void;
}

export function DeviceDetailPanel({ deviceId, onClose }: DeviceDetailPanelProps) {
  useLocale();
  const now = useLiveNow();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>("overview");
  const [confirmDeprovision, setConfirmDeprovision] = useState(false);

  const detail = useQuery({
    queryKey: ["devices", "detail", deviceId],
    queryFn: () => devicesApi.get(deviceId),
    refetchInterval: LIVE_REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: "always",
  });

  const forceRefresh = useMutation({
    mutationFn: () => devicesApi.forceRefresh(deviceId),
    onSuccess: () => {
      void invalidatePolicyViews(queryClient);
    },
  });

  const setStatus = useMutation({
    mutationFn: (target: AdminDeviceStatusTarget) => devicesApi.setStatus(deviceId, target),
    onSuccess: () => {
      void invalidatePolicyViews(queryClient);
    },
  });

  const subtitle = detail.data
    ? detail.data.user?.full_name ||
      detail.data.user?.email ||
      detail.data.model ||
      detail.data.device_uid
    : "";

  return (
    <>
      <div className="master-detail__panel-header">
        <div>
          <div className="muted" style={{ fontSize: 12 }}>
            {t("devices.detail.eyebrow")}
          </div>
          <h2 style={{ margin: "4px 0 2px" }}>{t("devices.detail.title")}</h2>
          {subtitle && (
            <div className="muted" style={{ fontSize: 14 }}>
              {subtitle}
            </div>
          )}
        </div>
        <button
          type="button"
          className="master-detail__panel-close"
          onClick={onClose}
          aria-label={t("common.close")}
        >
          &times;
        </button>
      </div>

      <div className="master-detail__panel-body">
        {detail.data && (
          <div className="row" style={{ gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
            <Badge tone={lifecycleTone(detail.data.status)}>
              {lifecycleStatusLabel(detail.data.status)}
            </Badge>
            <Button onClick={() => forceRefresh.mutate()} loading={forceRefresh.isPending}>
              {t("devices.actions.forceRefresh")}
            </Button>
            {detail.data.status === "active" && (
              <Button
                variant="secondary"
                onClick={() => setStatus.mutate("disabled")}
                loading={setStatus.isPending}
              >
                {t("devices.actions.disable")}
              </Button>
            )}
            {detail.data.status === "disabled" && (
              <Button onClick={() => setStatus.mutate("active")} loading={setStatus.isPending}>
                {t("devices.actions.activate")}
              </Button>
            )}
            {detail.data.status !== "deprovisioned" && (
              <Button
                variant="danger"
                onClick={() => setConfirmDeprovision(true)}
                loading={setStatus.isPending}
              >
                {t("devices.actions.deprovision")}
              </Button>
            )}
          </div>
        )}

        {forceRefresh.error && <Alert tone="danger">{translateError(forceRefresh.error)}</Alert>}
        {setStatus.error && <Alert tone="danger">{translateError(setStatus.error)}</Alert>}
        {forceRefresh.isSuccess && <Alert tone="info">{t("operations.policy.recompiled")}</Alert>}
        {detail.isLoading && <LoadingState />}
        {detail.error && (
          <ErrorState
            titleKey="devices.detail.notFoundTitle"
            message={translateError(detail.error)}
            action={
              <Button variant="secondary" size="sm" onClick={onClose}>
                {t("devices.detail.backToList")}
              </Button>
            }
          />
        )}

        <ConfirmDialog
          open={confirmDeprovision}
          title={t("devices.detail.deprovision.confirmTitle")}
          body={t("devices.detail.deprovision.confirmBody")}
          confirmLabel={t("devices.actions.deprovision")}
          tone="danger"
          loading={setStatus.isPending}
          onCancel={() => setConfirmDeprovision(false)}
          onConfirm={() => {
            setStatus.mutate("deprovisioned", {
              onSuccess: () => setConfirmDeprovision(false),
            });
          }}
        />

        {detail.data && (
          <>
            <DevicePolicyControl
              key={deviceId}
              device={detail.data}
              disabled={detail.isError || setStatus.isPending}
            />
            <Tabs<Tab>
              value={tab}
              onChange={setTab}
              tabs={[
                { value: "overview", label: t("devices.detail.tab.overview") },
                { value: "policy", label: t("devices.detail.tab.policy") },
              ]}
            />

            {tab === "overview" && (
              <>
                {detail.data.last_protection_revocation_reason ? (
                  <ProtectionRevocationBanner
                    reason={detail.data.last_protection_revocation_reason}
                    revokedAt={detail.data.last_protection_revocation_at}
                  />
                ) : null}
                <Card title={t("devices.detail.overview")}>
                  <DescriptionList
                    items={[
                      { labelKey: "devices.field.deviceUid", value: detail.data.device_uid },
                      {
                        labelKey: "devices.field.enrolleeName",
                        value: detail.data.enrollee?.full_name || "-",
                      },
                      {
                        labelKey: "devices.field.enrolleePhone",
                        value: detail.data.enrollee?.phone || "-",
                      },
                      {
                        labelKey: "devices.field.enrolleeEmployeeCode",
                        value: detail.data.enrollee?.employee_code || "-",
                      },
                      {
                        labelKey: "devices.field.linkedEmployee",
                        value: detail.data.employee
                          ? detail.data.employee.employee_id +
                            " (" +
                            t(
                              detail.data.employee.protection_active
                                ? "common.active"
                                : "common.inactive",
                            ) +
                            ")"
                          : "-",
                      },
                      {
                        labelKey: "devices.field.user",
                        value:
                          detail.data.user?.full_name || detail.data.user?.email || "-",
                      },
                      {
                        labelKey: "devices.field.lifecycle",
                        value: (
                          <Badge tone={lifecycleTone(detail.data.status)}>
                            {lifecycleStatusLabel(detail.data.status)}
                          </Badge>
                        ),
                      },
                      {
                        labelKey: "devices.field.manufacturer",
                        value: detail.data.manufacturer || "-",
                      },
                      {
                        labelKey: "devices.field.platform",
                        value: t("common.platform." + detail.data.platform),
                      },
                      { labelKey: "devices.field.model", value: detail.data.model || "-" },
                      {
                        labelKey: "devices.field.osVersion",
                        value: detail.data.os_version || "-",
                      },
                      {
                        labelKey: "devices.field.appVersion",
                        value: detail.data.app_version || "-",
                      },
                      {
                        labelKey: "devices.field.lastSeen",
                        value: (
                          <span>
                            {formatDateTime(detail.data.last_seen_at)}{" "}
                            <ConnectivityBadge
                              lastSeenAt={detail.data.last_seen_at}
                              isOnline={detail.data.is_online}
                              now={now}
                            />
                          </span>
                        ),
                      },
                      {
                        labelKey: "operations.policy.lastFetch",
                        value:
                          detail.data.last_policy_fetched_at === undefined
                            ? t("operations.policy.fetchUnsupported")
                            : detail.data.last_policy_fetched_at
                              ? formatDateTime(detail.data.last_policy_fetched_at)
                              : t("operations.policy.neverFetched"),
                      },
                      {
                        labelKey: "devices.field.isActive",
                        value: detail.data.is_active ? t("common.yes") : t("common.no"),
                      },
                      {
                        labelKey: "devices.field.timezone",
                        value: detail.data.timezone || "-",
                      },
                    ]}
                  />
                </Card>
              </>
            )}

            {tab === "policy" && (
              <div className="stack">
                <DevicePolicySection deviceId={deviceId} />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

function ProtectionRevocationBanner({
  reason,
  revokedAt,
}: {
  reason: string;
  revokedAt: string | null;
}) {
  const known = reason === "preempted_by_other_vpn" || reason === "user_revoked";
  const key = known ? reason : "unknown";
  return (
    <div style={{ marginBottom: 16 }}>
      <Card title={t("devices.detail.vpnRevocation.cardTitle")}>
        <div className="stack" style={{ gap: 8 }}>
          <div className="row" style={{ gap: 8, alignItems: "center" }}>
            <Badge tone="warning">
              {t("devices.detail.vpnRevocation." + key, reason)}
            </Badge>
            {revokedAt ? (
              <span className="muted" style={{ fontSize: 13 }}>
                {t("devices.detail.vpnRevocation.since")}: {formatDateTime(revokedAt)}
              </span>
            ) : null}
          </div>
          <div style={{ fontSize: 14 }}>
            {t("devices.detail.vpnRevocation." + key)}
          </div>
        </div>
      </Card>
    </div>
  );
}

function DevicePolicySection({ deviceId }: { deviceId: string }) {
  const workSchedule = useQuery({
    queryKey: ["device", deviceId, "work-schedule"],
    queryFn: () => devicesApi.getWorkSchedule(deviceId),
  });
  const policy = useQuery({
    queryKey: ["device", deviceId, "network-policy"],
    queryFn: () => deviceNetworkPolicyApi.get(deviceId),
    refetchInterval: LIVE_REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: "always",
  });

  return (
    <>
      <Card title={t("devices.detail.workSchedule.cardTitle")}>
        {workSchedule.isLoading && <LoadingState />}
        {workSchedule.error && (
          <ErrorState
            titleKey="states.errorTitle"
            message={translateError(workSchedule.error)}
          />
        )}
        {workSchedule.data && (
          <DescriptionList
            items={[
              {
                labelKey: "devices.detail.workSchedule.profile",
                value: workSchedule.data.profile_id ? (
                  <Link
                    to={
                      "/policies/schedules?id=" +
                      encodeURIComponent(workSchedule.data.profile_id)
                    }
                  >
                    {workSchedule.data.profile_name}
                  </Link>
                ) : (
                  "-"
                ),
              },
              {
                labelKey: "devices.detail.workSchedule.source",
                value: t(
                  "devices.detail.workSchedule.sourceValue." + workSchedule.data.source,
                ),
              },
              ...(workSchedule.data.target_label
                ? [
                    {
                      labelKey: "devices.detail.workSchedule.target",
                      value: workSchedule.data.target_label,
                    },
                  ]
                : []),
              ...(workSchedule.data.is_company_fallback
                ? [
                    {
                      labelKey: "devices.detail.workSchedule.fallback",
                      value: <Badge tone="info">{t("schedules.list.defaultBadge")}</Badge>,
                    },
                  ]
                : []),
            ]}
          />
        )}
      </Card>

      <Card title={t("operations.policy.networkPolicy")}>
        {policy.isLoading && <LoadingState />}
        {policy.error && (
          <ErrorState titleKey="states.errorTitle" message={translateError(policy.error)} />
        )}
        {policy.data && (
          <DescriptionList
            items={[
              {
                labelKey: "policies.compiled.version",
                value: <Badge tone="info">v{policy.data.version}</Badge>,
              },
              {
                labelKey: "policies.compiled.hash",
                value: (
                  <code style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>
                    {policy.data.policy_hash || policy.data.payload_hash}
                  </code>
                ),
              },
              {
                labelKey: "policies.compiled.compiledAt",
                value: formatDateTime(policy.data.compiled_at),
              },
              {
                labelKey: "policies.compiled.reason",
                value: policy.data.compiled_by_reason || "-",
              },
              {
                labelKey: "policies.compiled.ack",
                value: policy.data.acknowledged_at ? (
                  <Badge tone="success">
                    {t("policies.compiled.acked")}
                    {policy.data.acknowledged_app_version
                      ? " · " + policy.data.acknowledged_app_version
                      : ""}
                  </Badge>
                ) : (
                  <Badge tone="neutral">{t("policies.compiled.notAcked")}</Badge>
                ),
              },
            ]}
          />
        )}
      </Card>

      {policy.data && (
        <Card title={t("policies.payload.cardTitle")}>
          <JsonViewer value={policy.data.payload} defaultCollapsed />
        </Card>
      )}
    </>
  );
}
